import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageSquare, Phone, MapPin } from 'lucide-react';

const AREA_BLURBS = {
  kingman: 'Kingman is the seat of Mohave County in northwestern Arizona, set in the high desert between the Hualapai and Cerbat mountains along historic Route 66. The city offers parks, hiking trails, local shops and dining, with Interstate 40 and US-93 connecting it to the wider region.',
};
const fallbackBlurb = (city) => `${city || 'This area'} offers its own mix of neighborhoods, local businesses and everyday conveniences. Explore the map below to see what's nearby.`;

const num = (v) => (v ? Number(v).toLocaleString() : null);

// Price only appears when the listing has show_price on; other facts show "—" until filled in.
export const KeyDetails = ({ data }) => {
  const items = [
    ['Beds', 'beds', data.beds], ['Baths', 'baths', data.baths], ['Sq ft', 'sq-ft', num(data.square_feet)],
    ['Lot (sq ft)', 'lot', num(data.lot_square_feet)], ['Year built', 'year-built', data.year_built],
    ...(data.show_price ? [['Price', 'price', data.price ? `$${num(data.price)}` : null]] : []),
  ];
  return (
    <section className="border-y border-white/10 bg-[#0a1120]" data-testid="listing-key-details">
      <dl className={`max-w-7xl mx-auto px-2 sm:px-6 grid grid-cols-3 ${items.length > 5 ? 'sm:grid-cols-6' : 'sm:grid-cols-5'}`}>
        {items.map(([label, key, value]) => (
          <div key={key} className="py-5 sm:py-8 px-2 sm:px-4 text-center border-white/10 sm:border-l sm:first:border-l-0" data-testid={`listing-detail-${key}`}>
            <dt className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-[#C6A14A]/80">{label}</dt>
            <dd className={`font-serif text-2xl sm:text-3xl mt-2 ${value ? 'text-white' : 'text-[#E8E4DE]/35'}`}>{value || '—'}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

export const Highlights = ({ items }) => (items?.length ? (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14" data-testid="listing-highlights">
    <p className="text-xs uppercase tracking-[0.3em] text-[#C6A14A] mb-4">Highlights</p>
    <ul className="flex flex-wrap gap-2 sm:gap-3">
      {items.map((h, i) => (
        <li key={h} data-testid={`listing-highlight-${i}`}
          className="px-4 py-2 rounded-full border border-[#C6A14A]/35 bg-[#C6A14A]/[0.06] text-sm text-[#E8E4DE] animate-in fade-in slide-in-from-bottom-1 duration-500"
          style={{ animationDelay: `${i * 40}ms`, animationFillMode: 'both' }}>{h}</li>
      ))}
    </ul>
  </section>
) : null);

export const AreaSection = ({ fullAddress, city }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24 grid lg:grid-cols-[2fr_3fr] gap-8 lg:gap-12 items-start" data-testid="listing-area">
    <div>
      <p className="text-xs uppercase tracking-[0.3em] text-[#C6A14A]">The area</p>
      <h2 className="font-serif text-3xl sm:text-4xl text-white mt-2">{city || 'Location'}</h2>
      <p className="text-[#E8E4DE]/70 leading-relaxed mt-5" data-testid="listing-area-blurb">{AREA_BLURBS[(city || '').toLowerCase()] || fallbackBlurb(city)}</p>
      <p className="flex items-center gap-2 text-sm text-[#E8E4DE]/50 mt-5"><MapPin className="h-4 w-4 text-[#C6A14A]" />{fullAddress}</p>
    </div>
    <div className="rounded-2xl overflow-hidden border border-[#C6A14A]/25 aspect-[4/3] sm:aspect-[16/10] bg-[#0f1724]">
      <iframe title={`Map of ${fullAddress}`} src={`https://www.google.com/maps?q=${encodeURIComponent(fullAddress)}&output=embed`}
        className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-testid="listing-map" />
    </div>
  </section>
);

const AGENT = { name: 'Agent Name', brokerage: 'Brokerage', phone: '(555) 555-5555', email: 'agent@example.com' };
const contactBtn = 'inline-flex items-center justify-center gap-2 h-12 px-5 rounded-full text-sm font-medium transition-colors';

export const AgentContact = ({ agent = AGENT }) => {
  const tel = (agent.phone || '').replace(/[^\d+]/g, '');
  return (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24" data-testid="listing-agent">
    <div className="rounded-2xl border border-[#C6A14A]/30 bg-[#0a1120] p-6 sm:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#C6A14A]">Listed by</p>
        <p className="font-serif text-3xl text-white mt-2" data-testid="listing-agent-name">{agent.name}</p>
        <p className="text-[#E8E4DE]/60 mt-1" data-testid="listing-agent-brokerage">{agent.brokerage}</p>
        <p className="text-sm text-[#E8E4DE]/50 mt-3">{agent.phone} · {agent.email}</p>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:gap-3 md:flex">
        <a href={`tel:${tel}`} className={`${contactBtn} bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A]`} data-testid="listing-agent-call-btn"><Phone className="h-4 w-4" />Call</a>
        <a href={`sms:${tel}`} className={`${contactBtn} border border-[#C6A14A]/50 text-[#C6A14A] hover:bg-[#C6A14A]/10`} data-testid="listing-agent-text-btn"><MessageSquare className="h-4 w-4" />Text</a>
        <a href={`mailto:${agent.email}`} className={`${contactBtn} border border-[#C6A14A]/50 text-[#C6A14A] hover:bg-[#C6A14A]/10`} data-testid="listing-agent-email-btn"><Mail className="h-4 w-4" />Email</a>
      </div>
    </div>
  </section>
  );
};

export const ListingFooter = () => (
  <footer className="border-t border-white/10 bg-[#03050f]" data-testid="listing-footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
      <p className="text-sm text-[#E8E4DE]/60">Photos by <span className="font-serif text-[#C6A14A]">RC Studios</span></p>
      <Link to="/book" className="inline-flex items-center h-11 px-6 rounded-full border border-[#C6A14A]/60 text-[#C6A14A] text-sm hover:bg-[#C6A14A] hover:text-[#050816] transition-colors" data-testid="listing-book-rc-btn">Book RC Studios</Link>
    </div>
  </footer>
);
